import { createSelector } from 'reselect';

import { v1 as uuidv1 } from 'uuid';
import { RootState } from '~/store';
import {
  calculateTotalMaxDiscount,
  checkProductIsAboveDiscountAllowed,
  checkProductIsBelowMinimumPrice,
  comparePrices,
  getMargin,
  getPriceTable,
  setPaymentOptionsAndPlans,
  sort
} from './order-utils';
import { toDecimals } from '~/utils/format';
import { selectGlobal } from '~/store/modules/global/global-selector';
import {
  ClientInterface,
  ConfigurationInterface,
  OrderTypeInterface,
  PaymentOptionInterface,
  PaymentPlanInterface,
  ReasonInterface,
  UserInterface
} from '@e3labs/isa-data-dictionary';
import {
  OrderCreateInterface,
  OrderItemCreateInterface,
  OrderPromotionInterface,
  PromotionWithItemsInterface
} from './order-interface';
import { ORDER_TYPE, REASON_ACTIONS, SALE_TYPE, USER_ROLE } from '~/constants';
import { formatMessage } from '~/i18n/translate';
import { add, compareAsc } from 'date-fns';
import { getPromotionName } from '../promotion/promotion-utils';
import { calculateCommissionInfo } from '~/utils/calculateCommission';
import orderService from './order-service';

const selectOrder = (state: RootState) => state.order.form;
const selectOrderClient = (state: RootState) => state.order.form.data.client;

export const selectorOrderClient = () => createSelector(selectOrderClient, (substate: ClientInterface) => substate);

export const selectorPaymentOptions = (client: ClientInterface, defaultCode: string | null = null) =>
  createSelector(selectOrder, (substate: { data: OrderCreateInterface; selectsDefault: any; selects: any }) => {
    const { data } = substate;

    const defaultPaymentOptions: PaymentOptionInterface[] = substate?.selectsDefault?.paymentOptions;
    const subsidiariesCharge: any[] = substate?.selects?.subsidiariesCharge;

    const {
      userExternalCode,
      orderType,
      orderDetails: { subsidiary, paymentOption: selectedPaymentOption }
    } = data;

    let defaultPaymentOption: any =
      defaultPaymentOptions.find(option =>
        defaultCode ? option.externalCode === defaultCode : option.externalCode === client?.paymentOptionExternalCode
      ) ?? null;

    let paymentOptions: PaymentOptionInterface[] = defaultPaymentOptions;

    if (orderType.type !== ORDER_TYPE.BONIFICATION && orderType.type !== ORDER_TYPE.BONIFICATION_FREE_TAX) {
      paymentOptions = paymentOptions.filter(
        option =>
          (!defaultPaymentOption || option.sellLevel >= defaultPaymentOption.sellLevel) &&
          !['BNF', 'BNFT', 'BNTF'].includes(option.externalCode)
      );
    }

    if (orderType.type === ORDER_TYPE.BONIFICATION || orderType.type === ORDER_TYPE.BONIFICATION_FREE_TAX) {
      paymentOptions = paymentOptions.filter(option => ['BNF', 'BNFT', 'BNTF'].includes(option.externalCode));
    }

    paymentOptions = paymentOptions.filter(
      option =>
        (option.subsidiaryExternalCode === subsidiary?.externalCode || option.subsidiaryExternalCode === null) &&
        option.sendsSalesForce === true
    );

    let subsidiaryCharge = subsidiariesCharge?.find(
      item =>
        item?.clientExternalCode === client?.externalCode &&
        (!item?.userExternalCode || item.userExternalCode === userExternalCode) &&
        item.subsidiaryExternalCode === subsidiary?.externalCode
    );
    if (subsidiaryCharge) {
      const paymentOptionExternalCodes = subsidiaryCharge?.paymentOptionExternalCodes ?? [];

      defaultPaymentOption =
        defaultPaymentOptions.find(option => option.externalCode === paymentOptionExternalCodes[0]) ?? null;
      paymentOptions = [
        ...defaultPaymentOptions.filter(option => paymentOptionExternalCodes?.includes(option.externalCode))
      ];
    } else {
      defaultPaymentOption = !!paymentOptions.find(
        option => option.externalCode === selectedPaymentOption?.externalCode
      )
        ? selectedPaymentOption
        : paymentOptions.find(option =>
            defaultCode
              ? option.externalCode === defaultCode
              : option.externalCode === client?.paymentOptionExternalCode
          ) ?? paymentOptions[0];
    }

    defaultPaymentOption = defaultPaymentOption ?? defaultPaymentOptions[0];
    return { paymentOption: defaultPaymentOption, paymentOptions };
  });

export const selectorPaymentPlans = (
  client: ClientInterface,
  paymentOption: PaymentOptionInterface,
  clientReasons: ReasonInterface,
  orderType: OrderTypeInterface,
  hidePaymentPlanExternalCodes: boolean,
  defaultCode: string | null = null
) =>
  createSelector(selectOrder, (substate: { data: OrderCreateInterface; selectsDefault: any }) => {
    const { orderDetails } = substate.data;

    let paymentPlans: PaymentPlanInterface[] = substate.selectsDefault.paymentPlans;
    let defaultPaymentPlans: PaymentPlanInterface[] = substate.selectsDefault.paymentPlans;

    let paymentPlan;
    let paymentPlanGroup;

    const reasons: any = clientReasons ? Object.values(clientReasons) : [];

    const multiplesSubsidiariesPaymentPlan = paymentPlans?.filter(paymentPlan =>
      paymentPlan?.multiplesSubsidiaries?.includes(orderDetails?.subsidiary?.externalCode)
    );

    if (!!paymentOption?.paymentPlanExternalCodes?.length) {
      paymentPlans = paymentPlans.filter(plan => paymentOption?.paymentPlanExternalCodes?.includes(plan.externalCode));
    }

    paymentPlans = [
      ...paymentPlans.filter(
        plan =>
          (plan?.multiplesSubsidiaries?.length === 0 || plan?.multiplesSubsidiaries === undefined) &&
          plan.averageTerm <= (paymentOption?.deadlineSale || 99999) &&
          (orderDetails.orderType === ORDER_TYPE.BONIFICATION ||
          orderDetails.orderType === ORDER_TYPE.BONIFICATION_FREE_TAX
            ? plan.termType === 'B'
            : plan.termType !== 'B') &&
          (plan.subsidiaryExternalCode === orderDetails?.subsidiary?.externalCode ||
            plan.subsidiaryExternalCode === null) &&
          (!!plan?.clientBlocked?.length ? !plan?.clientBlocked?.includes(client!?.externalCode) : true) &&
          (!!plan.clientRestriction?.length ? plan?.clientRestriction?.includes(client!?.externalCode) : true) &&
          (!!plan.providerRestriction?.length
            ? plan?.providerRestriction?.includes(client!?.externalCode)
              ? plan?.providerRestriction?.includes(client!?.externalCode)
              : true
            : true) &&
          plan.enabled === true
      )
    ];

    let paymentPlanDefaultExternalCode;

    if (defaultCode) {
      paymentPlanDefaultExternalCode = defaultCode;
    } else if (client?.usePaymentPlansGroup) {
      paymentPlanDefaultExternalCode = client?.paymentPlanGroup1ExternalCode;
    } else if (client?.paymentPlanExternalCode) {
      paymentPlanDefaultExternalCode = client?.paymentPlanExternalCode;
    }

    if (paymentPlanDefaultExternalCode && orderDetails.orderType !== ORDER_TYPE.BONIFICATION) {
      const defaultPlan = defaultPaymentPlans?.filter(plan => plan.externalCode === paymentPlanDefaultExternalCode);

      if (defaultPlan?.length > 0) {
        paymentPlans = [...paymentPlans.filter(plan => plan.averageTerm <= defaultPlan[0]?.averageTerm)];
      }
    }

    /* if (client?.paymentPlanExternalCode && orderDetails.orderType !== ORDER_TYPE.BONIFICATION) {
      paymentPlan = [...paymentPlans.filter(plan => plan?.externalCode === client?.paymentPlanExternalCode)];


      if (paymentPlan.length > 0) {
        paymentPlans = [...paymentPlans.filter(plan => plan.averageTerm <= (paymentOption?.deadlineSale || 99999))];
      }

    } */

    const anlyCashAllowed =
      reasons?.some(reason => reason?.actions?.orders === REASON_ACTIONS.ANLY_CASH_ALLOWED) ?? false;

    if (anlyCashAllowed) {
      paymentPlans = [...paymentPlans.filter(plan => plan.saleType === SALE_TYPE.CASH || plan.saleType === null)];
    }

    if (orderType?.paymentPlanExternalCodes && orderType?.paymentPlanExternalCodes?.length > 0) {
      paymentPlans = [
        ...paymentPlans.filter(plan => orderType?.paymentPlanExternalCodes?.includes(plan?.externalCode))
      ];
    }

    if (multiplesSubsidiariesPaymentPlan?.length > 0) {
      paymentPlans = [...paymentPlans, ...multiplesSubsidiariesPaymentPlan];
    }

    if (paymentOption) {
      const currentPaymentPlan = paymentPlans.find(
        plan => plan.externalCode === orderDetails?.paymentPlan?.externalCode
      );

      const defaultPaymentPlan = paymentPlans.find(plan => plan.externalCode === client?.paymentPlanExternalCode);

      paymentPlan = !client?.usePaymentPlansGroup ? currentPaymentPlan ?? defaultPaymentPlan ?? paymentPlans[0] : null;

      if (defaultCode) {
        paymentPlan = paymentPlans?.find(plan => plan.externalCode === defaultCode) ?? paymentPlan;
      }

      paymentPlanGroup = client?.usePaymentPlansGroup
        ? {
            paymentPlan1:
              paymentPlans?.find(plan => plan?.externalCode === client?.paymentPlanGroup1ExternalCode) ??
              paymentPlans[0],

            paymentPlan2:
              paymentPlans?.find(plan => plan?.externalCode === client?.paymentPlanGroup2ExternalCode) ??
              paymentPlans[0]
          }
        : null;
    } else {
      paymentPlan = null;
      paymentPlanGroup = null;
    }

    return {
      paymentPlan,
      paymentPlanGroup,
      paymentPlans: sort(paymentPlans, false, hidePaymentPlanExternalCodes) ?? []
    };
  });

export const validateBudgetCopy = (order, availablePromotions) =>
  createSelector(selectGlobal, substate => {
    const { configuration }: { configuration: ConfigurationInterface } = substate;

    let validateOrderItemsErrors = {
      quantity: 0,
      messages: {}
    };

    order?.orderItems?.forEach((orderItem, index) => {
      if (availablePromotions[orderItem.externalCode]) {
        const oldPromotion =
          orderItem?.promotion?.promotionExternalCode ||
          orderItem?.promotion?.promotionMedCode ||
          orderItem?.promotion?.codpromocaomed ||
          orderItem?.promotion?.externalCode
            ? orderItem?.promotion
            : null;

        orderItem.promotions = availablePromotions[orderItem.externalCode];

        orderItem.isInvalidPromotion = false;

        if (oldPromotion) {
          orderItem.promotion = availablePromotions[orderItem.externalCode].find(promotion => {
            const promotionExternalCode = promotion?.codpromocaomed ?? promotion?.externalCode;
            const orderItemExternalCode =
              orderItem.promotion?.promotionMedCode ?? orderItem.promotion?.promotionExternalCode;

            return promotionExternalCode === orderItemExternalCode?.toString();
          });

          if (orderItem?.promotion?.isPromotionKit) {
            orderItem.promotion = { ...orderItem?.promotion, ...oldPromotion };
          }

          if (!orderItem.promotion) {
            orderItem.isInvalidPromotion = true;

            validateOrderItemsErrors = {
              ...validateOrderItemsErrors,
              quantity: validateOrderItemsErrors.quantity + 1,
              messages: {
                ...validateOrderItemsErrors.messages,
                [`orderItem${index}`]: formatMessage(
                  configuration.blockSaleWithoutPromotion
                    ? 'orderDetailInvalidPromotionsWithBlock'
                    : 'orderDetailInvalidPromotions',
                  {
                    externalCode: orderItem.productExternalCode,
                    name: orderItem.name
                  }
                )
              }
            };
          }
        } else {
          orderItem.promotion = null;
        }
      }
    });

    if (validateOrderItemsErrors?.quantity >= order.orderItems.length) {
      validateOrderItemsErrors = {
        ...validateOrderItemsErrors,
        messages: {
          ...validateOrderItemsErrors.messages,
          [`orderWithoutValidOrderItems`]: formatMessage('orderDetailWithoutValidOrderItems')
        }
      };
    }

    if (validateOrderItemsErrors?.quantity && configuration.blockSaleWithoutPromotion) {
      validateOrderItemsErrors = {
        ...validateOrderItemsErrors,
        messages: {
          ...validateOrderItemsErrors.messages,
          [`orderCartIsLessQuantity`]: formatMessage('orderDetailCartIsLessQuantity', {
            initialQuantity: order.orderItems.length,
            currentQuantity: order.orderItems.filter(orderItem => !orderItem.isInvalidPromotion).length
          })
        }
      };
    }

    return {
      ...order,
      validateOrderItemsErrors: {
        ...validateOrderItemsErrors,
        messages: [...Object.entries(validateOrderItemsErrors.messages).flat(Infinity)].filter(
          (item, index) => index % 2 !== 0
        )
      }
    };
  });

export const formatOrder = (
  order,
  selectsDefault,
  copy = false,
  bonification = false,
  complementary = false,
  childOrderExternalNumber = undefined,
  orderVinculatedExternalNumber = undefined,
  isEdit = false,
  userRole = undefined,
  commissions,
  client,
  priceTableCode,
  userAccountBalance = false
) =>
  createSelector(selectGlobal, substate => {
    const { configuration }: { configuration: ConfigurationInterface } = substate;

    const { subsidiaries, subsidiaryStocks, paymentOptions, paymentPlans, orderTypes, deliveries } = selectsDefault;

    const formatOrderItem = orderItem => {
      const {
        soldPrice: oldSoldPrice,
        soldQuantity,
        isSponsored,
        isRecommendation,
        tributacao,
        tabulatedPrice,
        baseValue: oldBaseValue,
        financialCost,
        priorityPolice,
        bonificated,
        discountPercentage,
        productExternalCode,
        availableQuantity,
        percentualRate,
        promotions,
        package: packing,
        packing: packings,
        assistedSaleType,
        ...productInfo
      } = orderItem;

      if (orderItem?.isInvalidPromotion && configuration.blockSaleWithoutPromotion) {
        return;
      }

      let soldPrice = oldSoldPrice;
      let baseValue = oldBaseValue;
      let soldPriceWithPacking = orderItem?.soldPriceWithPacking ?? orderItem?.soldPrice;
      let baseValueWithPacking = orderItem?.baseValueWithPacking ?? 0;
      let totalPriceWithPacking = orderItem?.totalPriceWithPacking ?? orderItem?.totalPrice;

      let parsedDiscountPercentage = toDecimals(discountPercentage, configuration?.decimals ?? 2);

      let maxDiscountPercentage = orderItem?.maxDiscountPercentage ?? orderItem?.price?.maxDiscountPercentage ?? 0;
      let minimumPrice = toDecimals(
        baseValue - (baseValue * maxDiscountPercentage) / 100,
        configuration?.decimals ?? 2
      );

      let promotion =
        promotions?.find(
          item =>
            item.aggregatorExternalCode === orderItem?.promotion?.promotionExternalCode ||
            item.aggregatorExternalCode === orderItem?.promotion?.promotionMedCode ||
            item.externalCode === orderItem?.promotion?.promotionExternalCode
        ) ?? null;

      if (promotion) {
        if (promotion.isPromotionKit) {
          promotion = {
            ...promotion,
            promotionKitQuantity: promotion?.promotionKitQuantity ?? promotion?.promotionKitQuantity
          };
        }
        minimumPrice = toDecimals(
          (baseValue * (100 - (promotion?.maxDiscountPercentage ?? promotion?.discountPercentage ?? 0))) / 100,
          configuration?.decimals ?? 2
        );
      } else if (promotions?.length > 0) {
        const minimumPriceTable = toDecimals(
          (baseValue * (100 - maxDiscountPercentage)) / 100,
          configuration?.decimals ?? 2
        );

        const minimumPricePromotion = promotions?.reduce((menor, atual) => {
          return atual?.minimumPrice < menor?.minimumPrice ? atual : menor;
        });

        minimumPrice = [
          minimumPricePromotion?.minimumPrice,
          configuration.useDiscountControl ? minimumPriceTable : orderItem.baseValue
        ].sort((a, b) => a - b)[0];
      } else {
        minimumPrice = toDecimals(baseValue - (baseValue * maxDiscountPercentage) / 100, configuration?.decimals ?? 2);

        if (soldPriceWithPacking === baseValue) {
          minimumPrice = toDecimals(
            soldPriceWithPacking / (packing?.packingQuantity || 1),
            configuration?.decimals ?? 2
          );
        }
      }

      if (promotion?.typeBalance) {
        const totalDiscount = calculateTotalMaxDiscount({
          applyCombinedDiscount: configuration?.applyCombinedDiscount,
          applySequentialDiscountsForMinimumPrice: configuration?.applySequentialDiscountsForMinimumPrice,
          promotionDiscount: promotion.discountPercentage,
          maxDiscount: maxDiscountPercentage,
          baseValue,
          decimals: configuration?.decimals
        });
        minimumPrice = toDecimals((baseValue * (100 - totalDiscount)) / 100, configuration.decimals ?? 2);
      }

      if (configuration.useAutomaticPricingRetailOrWholesale) {
        const paymentPlanExternalCode = order?.client?.usePaymentPlansGroup
          ? order?.orderDetails?.paymentPlanGroup?.[`paymentPlan${orderItem?.product?.paymentPlansGroup}`]
          : order?.orderDetails?.paymentPlanExternalCode;

        const minimumProductWholeSaleQuantity = orderItem?.price?.minimumProductWholeSaleQuantity;
        const priceWholeSale =
          orderItem?.price[`priceWholeSale${paymentPlanExternalCode === '1' ? '' : paymentPlanExternalCode}`];

        if (minimumProductWholeSaleQuantity && orderItem?.soldQuantity >= minimumProductWholeSaleQuantity) {
          soldPrice = toDecimals(
            priceWholeSale * (packing?.priceFactor ?? 1) * (packing?.packingQuantity ?? 1),
            configuration.decimals ?? 2
          );
          minimumPrice = toDecimals(
            priceWholeSale * (packing?.priceFactor ?? 1) * (packing?.packingQuantity ?? 1),
            configuration.decimals ?? 2
          );
        }

        if (minimumProductWholeSaleQuantity && orderItem?.soldQuantity < minimumProductWholeSaleQuantity) {
          soldPrice = toDecimals(
            orderItem?.promotion?.minimumPrice ?? orderItem?.product?.baseValue,
            configuration.decimals ?? 2
          );
          minimumPrice = toDecimals(
            orderItem?.promotion?.minimumPrice ?? orderItem?.product?.baseValue,
            configuration.decimals ?? 2
          );
        }
      }

      const isBelowMinimumPrice = checkProductIsBelowMinimumPrice({
        isPromotionKit: orderItem?.promotion?.isPromotionKit,
        soldPrice,
        minimumPrice,
        acceptNegativeBalance: configuration?.acceptNegativeBalance && userAccountBalance,
        decimals: configuration?.decimals
      });

      const isAboveDiscountAllowed = checkProductIsAboveDiscountAllowed({
        discountPercentage: orderItem?.discountPercentage,
        promotionTypeBalance: promotion?.typeBalance,
        totalDiscount: promotion?.discountPercentage + orderItem?.maxDiscountPercentage,
        promotionMaxDiscountPercentage: promotion
          ? promotion?.maxDiscountPercentage ?? promotion?.discountPercentage
          : promotions?.[0]?.discountPercentage ?? 0,
        maxDiscountTotal: orderItems?.maxDiscountPercentage,
        acceptNegativeBalance: configuration?.acceptNegativeBalance && userAccountBalance
      });

      let barCode = productInfo?.barCode;

      if (packing) {
        barCode = packing?.barCode;
      }

      if (!packing && packings?.length > 0) {
        barCode = packings?.[0]?.barCode;
      }

      if (!barCode) {
        barCode = '0';
      }

      const commission = calculateCommissionInfo({
        allowUserCommission: configuration?.allowUserCommission,
        commissions,
        decimals: configuration?.decimals ?? 2,
        promotionCommission: promotion?.comissionPromo ?? 0,
        discountPercentage: parsedDiscountPercentage,
        soldPrice,
        soldQuantity
      });

      let subsidiaryStock = null;

      if (configuration?.useSubsidiaryStockByProduct) {
        subsidiaryStock =
          selectsDefault?.subsidiaries?.find(item => item?.externalCode === orderItem?.subsidiaryStockExternalCode) ??
          null;
      }

      const unitaryValue = orderService.buildUnitaryValue({
        baseValue: orderItem?.baseValue,
        discount: orderItem.discountPercentage ?? 0,
        precision: configuration.decimals ?? 2,
        promotion,
        priceFactor: packing?.priceFactor ?? 1
      });

      const cartId = uuidv1();

      const totalPriceProductKit = promotion?.isPromotionKit
        ? orderItem?.fixedPrice
          ? orderItem?.fixedPrice * soldQuantity
          : soldPrice * soldQuantity
        : null;

      const newProduct = {
        ...productInfo,
        ...orderItem,
        externalCode: productExternalCode,
        tributacao,
        baseValue: oldBaseValue,
        baseValueWithPacking,
        soldPriceWithPacking,
        totalPriceWithPacking,
        totalPriceProductKit,
        isEditOrderItem: isEdit,
        minimumPrice,
        isBelowMinimumPrice,
        isAboveDiscountAllowed,
        assistedSaleType,
        barCode,
        unitaryValue,
        cartId
      };

      delete newProduct.product;

      return {
        ...newProduct,
        baseValueWithPacking,
        soldPriceWithPacking,
        totalPriceWithPacking,
        totalPriceProductKit,
        margin: getMargin(soldPrice, financialCost, configuration.decimals),
        packingInfos: {
          ...(packing
            ? {
                unit: packing.packingUnit,
                availableQuantity: toDecimals(0 / packing?.packingQuantity, 2),
                barCode: packing.barCode
              }
            : {
                unit: orderItem.unit,
                availableQuantity,
                barCode
              }),
          packingPrice: soldPrice,
          basePackingPrice: toDecimals(
            (baseValue ?? 0) * (packing?.packingQuantity || 1),
            configuration?.decimals ?? 2
          ),
          unitaryValue
        },
        ...(orderItem.isInvalidPromotion
          ? {
              soldPrice: baseValue,
              totalPrice: baseValue * soldQuantity,
              discount: false,
              discountPercentage: 0,
              tributacao: {
                values: {
                  vlST: 0,
                  IPI: 0
                }
              },
              isPromotion: false
            }
          : {
              ...(promotion &&
                promotion?.externalCode && {
                  promotion: {
                    ...promotion,
                    promotionName: getPromotionName(promotion)
                  },
                  isPromotion: true
                }),
              soldPrice: soldPriceWithPacking || soldPrice,
              totalPrice: toDecimals((soldPriceWithPacking || soldPrice) * soldQuantity, configuration.decimals ?? 2),
              discountPercentage: parsedDiscountPercentage,
              discount: !!parsedDiscountPercentage,
              tributacao
            }),
        promotions: promotions?.map(promotion => ({ ...promotion, promotionName: getPromotionName(promotion) })),
        promotion,
        packing: packing ?? null,
        packings,
        barCode,
        soldQuantity,
        tabulatedPrice,
        baseValue,
        financialCost,
        percentualRate,
        priorityPolice,
        isBelowMinimumPrice,
        isAboveDiscountAllowed,
        minimumPrice,
        maximumPrice: toDecimals(
          baseValue + (baseValue * configuration?.maximumSalePercentage) / 100,
          configuration?.decimals ?? 2
        ),
        availableQuantity,
        isRecommendation,
        isSponsored,
        assistedSaleType,
        bonificated,
        orderTypeExternalCode: bonification
          ? orderTypes?.orderTypes?.find(x => x.type === 'bonification')?.externalCode
          : orderItem?.orderTypeExternalCode ?? null,
        orderType: bonification
          ? orderTypes?.orderTypes?.find(x => x.type === 'bonification')
          : bonificated && configuration?.splitBonusOrder
          ? orderTypes?.orderTypes?.find(x => x.externalCode === orderItem?.orderTypeExternalCode)
          : null,
        commission,
        subsidiaryStockExternalCode: orderItem?.subsidiaryStockExternalCode ?? null,
        subsidiaryStock,
        unitaryValue
      };
    };

    let preFormatOrderItems: any = [];
    order?.orderItems?.forEach(orderItem => {
      if (!(orderItem?.isInvalidPromotion && configuration.blockSaleWithoutPromotion)) {
        preFormatOrderItems = [...preFormatOrderItems, formatOrderItem(orderItem)];
      }
    });

    const comboKitTotals = preFormatOrderItems.reduce((acc, product) => {
      const aggregatorExternalCode = product.promotion?.aggregatorExternalCode;
      if (aggregatorExternalCode) {
        const valorAtual = product.soldQuantity * (product.promotion?.promotionalPrice || product.soldPrice);
        acc[aggregatorExternalCode] = (acc[aggregatorExternalCode] || 0) + valorAtual;
      }
      return acc;
    }, {});

    let orderItems: any = [];
    preFormatOrderItems.forEach(orderItem => {
      const totalSoldPriceKit = comboKitTotals[orderItem.promotion?.aggregatorExternalCode];
      orderItems = [
        ...orderItems,
        {
          ...orderItem,
          promotion: {
            ...orderItem.promotion,
            totalSoldPriceKit,
            promotionOriginalTotalPrice: totalSoldPriceKit / (orderItem.promotion?.promotionKitQuantity ?? 1)
          }
        }
      ];
    });
    const newClient = {
      ...order?.client,
      ...client,
      usePaymentPlansGroup: order.orderDetails.usePaymentPlansGroup
    };

    const subsidiary = subsidiaries.find(item =>
      order.orderDetails?.originalSubsidiaryExternalCode
        ? item.externalCode === order.orderDetails?.originalSubsidiaryExternalCode
        : item.externalCode === order.orderDetails?.subsidiaryExternalCode
    );

    const providerDeliveryExternalCode = order?.orderDetails?.providerDeliveryExternalCode
      ? selectsDefault.providerDelivery?.find(
          item => item.externalCode === order?.orderDetails?.providerDeliveryExternalCode
        )
      : null;

    const orderType: OrderTypeInterface = orderTypes.find(
      (x: OrderTypeInterface) =>
        x.type === order.orderDetails.orderType && x.externalCode === order.orderDetails.orderTypeExternalCode
    );

    const vendorDefault =
      userRole === USER_ROLE.TELEMARKETING
        ? {
            name: order?.vendor?.name,
            externalCode: order?.vendor?.externalCode
          }
        : undefined;

    const data: OrderCreateInterface = {
      realmID: order?.realmID,
      orderStatus: order?.orderStatus || '',
      ...(complementary
        ? {
            complementary: true,
            originalOrderNumber: order.orderNumber,
            originalOrderExternalNumber: order.orderExternalNumber
          }
        : {}),
      orderVinculatedExternalNumber:
        configuration?.allowVinculatedBonification &&
        !configuration?.allowBonificationOnPendingOrders &&
        orderVinculatedExternalNumber
          ? orderVinculatedExternalNumber
          : order?.orderVinculatedExternalNumber
          ? order?.orderVinculatedExternalNumber
          : undefined,
      parentInternalCode:
        configuration?.allowVinculatedBonification &&
        configuration?.allowBonificationOnPendingOrders &&
        childOrderExternalNumber
          ? order?.internalCode
          : null,
      orderDetails: {
        vendor: vendorDefault,
        creationDate: new Date(),
        orderType: orderType?.type,
        shippingType: order.orderDetails.shippingType,
        subsidiary,
        subsidiaryStock:
          subsidiaryStocks.find(item => item.externalCode === order.orderDetails?.subsidiaryStock) ||
          order.orderDetails?.subsidiaryStock,
        priceTableCode,
        ...setPaymentOptionsAndPlans({
          bonification,
          paymentOptions,
          paymentPlans,
          paymentOptionExternalCode: order.orderDetails.paymentOptionExternalCode,
          paymentPlanExternalCode: order.orderDetails.paymentPlanExternalCode,
          usePaymentPlansGroup: order.orderDetails.usePaymentPlansGroup,
          paymentPlanGroup1ExternalCode: order.orderDetails.paymentPlanGroup1ExternalCode,
          paymentPlanGroup2ExternalCode: order.orderDetails.paymentPlanGroup2ExternalCode
        }),
        deliveryExternalCode: order.orderDetails.deliveryExternalCode,
        bonificationApprovalSwitch: !!childOrderExternalNumber ? true : false,
        promoterExternalCode: order.orderDetails.promoterExternalCode ?? null,
        otherExpenses: order.orderDetails.otherExpenses,
        bonificationTypeValue: order.orderDetails.bonificationTypeValue,
        providerDeliveryExternalCode,
        orderObservation: order.orderDetails.orderObservation,
        orderNumber: order.orderDetails.clientOrderNumber,
        clientOrderNumber: order.orderDetails.clientOrderNumber,
        deliveryObservation: order.orderDetails.deliveryObservation,
        clientGovernmentalCode: order.orderDetails.clientGovernmentalCode
      },
      ...(order.budget.isBudget && compareAsc(new Date(), new Date(order.budget.expirationDate)) < 0
        ? {
            budget: {
              ...order.budget,
              budgetID: new Date().getTime(),
              effectedSale: false,
              isBudget: order.budget?.isBudget && !order.budget?.effectedSale,
              expirationDate: add(new Date(), { days: 2 })
            },
            budgetCopyID: copy ? false : order.budget?.budgetID
          }
        : {
            budget: {
              isBudget: false,
              effectedSale: false,
              expirationDate: add(new Date(), { days: 2 }),
              budgetID: new Date().getTime()
            }
          }),
      fromBudget: copy ? false : order.budget?.isBudget,
      originalBudget: order.budget,
      _id: order._id,
      isCopy: copy,
      orderType,
      delivery: deliveries.find(item => item.externalCode === order.orderDetails.deliveryExternalCode),
      client: newClient,
      shipping: {
        ...order.shipping
      },
      orderItems:
        bonification && orderVinculatedExternalNumber
          ? []
          : childOrderExternalNumber
          ? []
          : complementary
          ? []
          : orderItems,
      totalPrice:
        bonification && orderVinculatedExternalNumber
          ? 0
          : childOrderExternalNumber
          ? 0
          : orderItems?.reduce(
              (accumulator, orderItem) => accumulator + orderItem.soldPrice * orderItem?.soldQuantity,
              0
            ),
      totalQuantity:
        bonification && orderVinculatedExternalNumber
          ? 0
          : childOrderExternalNumber
          ? 0
          : orderItems?.reduce((accumulator, orderItem) => accumulator + orderItem?.soldQuantity, 0),
      load: order.load,
      userExternalCode: order.userExternalCode,
      clientExternalCode: order.client.externalCode
    } as OrderCreateInterface;
    return data;
  });

export const combinePromotions = () =>
  createSelector(selectOrder, (substate: { data: OrderCreateInterface }) => {
    const getTotals = (ordemItems: OrderItemCreateInterface[]) => {
      return ordemItems.reduce(
        (total, item) => ({
          totalPrice: (total.totalPrice += item.soldPrice * item.soldQuantity),
          totalQuantity: (total.totalQuantity += item.soldQuantity)
        }),
        { totalPrice: 0, totalQuantity: 0 }
      );
    };

    const getValidatedPromotion = (
      promotion: OrderPromotionInterface | null | undefined,
      { totalQuantity, totalPrice }: { totalQuantity: number; totalPrice: number }
    ) => {
      return promotion
        ? {
            isWithinQuantityOfPromotion:
              promotion?.initialQuantity && promotion?.finalQuantity
                ? (!promotion?.initialQuantity && totalQuantity <= promotion?.finalQuantity) ||
                  (totalQuantity >= promotion?.initialQuantityy && !promotion?.finalQuantity) ||
                  (totalQuantity >= promotion?.initialQuantity && totalQuantity <= promotion?.finalQuantity)
                : true,
            isAboveMinimumValueOfPromotion: promotion?.minimumPromotionValue
              ? totalPrice >= promotion?.minimumPromotionValue
              : true
          }
        : {
            isAboveMinimumValueOfPromotion: true,
            isWithinQuantityOfPromotion: true
          };
    };

    const promotionWithItems = substate?.data.orderItems?.reduce(
      (arrayPromotions: PromotionWithItemsInterface[], orderItem: OrderItemCreateInterface) => {
        const itemPromotion = arrayPromotions?.find(
          (somePromotion: PromotionWithItemsInterface) =>
            somePromotion.promotion?.externalCode === orderItem?.promotion?.externalCode
        );

        if (itemPromotion && itemPromotion.isPromotionGroup) {
          const index = arrayPromotions?.indexOf(itemPromotion);
          const { promotion, orderItems } = itemPromotion;
          const combinedItems = [...orderItems, orderItem];
          const { totalQuantity, totalPrice } = getTotals(combinedItems);
          const { isAboveMinimumValueOfPromotion, isWithinQuantityOfPromotion } = getValidatedPromotion(promotion, {
            totalQuantity,
            totalPrice
          });

          arrayPromotions[index] = {
            promotion,
            isPromotionGroup: false,
            isAboveMinimumValueOfPromotion,
            isWithinQuantityOfPromotion,
            orderItems: [...combinedItems],
            totalQuantity,
            totalPrice
          };

          return [...arrayPromotions];
        } else {
          const combinedItems = [orderItem];
          const { totalQuantity, totalPrice } = getTotals(combinedItems);

          const promotion = orderItem.promotion;
          const { isAboveMinimumValueOfPromotion, isWithinQuantityOfPromotion } = getValidatedPromotion(promotion, {
            totalQuantity,
            totalPrice
          });
          return [
            ...arrayPromotions,
            {
              promotion,
              isPromotionGroup: false,
              isAboveMinimumValueOfPromotion,
              isWithinQuantityOfPromotion,
              orderItems: [...combinedItems],
              totalQuantity,
              totalPrice
            }
          ];
        }
      },
      []
    );

    return promotionWithItems;
  });
